import { useEffect, useRef, useState } from 'react'
import { Camera, ArrowCounterClockwise } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { compressImage, PHOTO_SIZES, type PhotoKind } from '@/lib/image'
import { discardMedia, getMediaBlob, saveMedia } from '@/offline/media'
import { Button } from './Button'
import { Spinner } from './Spinner'
import { useFieldControl } from './fieldContext'

export interface PhotoCaptureProps {
  /** Media id (UUIDv7) of the stored photo, or null. Put this id in the command payload. */
  value: string | null
  onChange: (mediaId: string | null) => void
  disabled?: boolean
  /** How big the photo is kept (see PHOTO_SIZES). */
  kind?: PhotoKind
}

/**
 * Opens the rear camera, compresses to JPEG, stores the blob in IndexedDB (`media` table) and
 * returns its id. The sync engine uploads it with PUT /media/{id}. Nothing leaves the phone here.
 */
export function PhotoCapture({ value, onChange, disabled, kind = 'proof' }: PhotoCaptureProps) {
  const { t } = useI18n()
  const field = useFieldControl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!value) return
    let url: string | null = null
    let alive = true
    void getMediaBlob(value).then((blob) => {
      if (!alive || !blob) return
      url = URL.createObjectURL(blob)
      setPreview(url)
    })
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
      setPreview(null)
    }
  }, [value])

  const onFile = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    setFailed(false)
    try {
      const blob = await compressImage(file, PHOTO_SIZES[kind])
      const id = await saveMedia(blob)
      if (value) await discardMedia(value)
      onChange(id)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        id={field?.id}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
      {value && preview ? (
        <div className="flex items-center gap-3">
          <img
            src={preview}
            alt=""
            className="h-20 w-20 rounded-card border border-rule object-cover"
          />
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-[13px] text-ok">{t('photo.saved')}</span>
            <Button
              variant="secondary"
              size="touch"
              leadingIcon={<ArrowCounterClockwise aria-hidden="true" />}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || busy}
              loading={busy}
            >
              {t('photo.retake')}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          aria-describedby={field?.describedBy}
          className={cn(
            'flex min-h-[88px] w-full flex-col items-center justify-center gap-1 rounded-card border border-dashed bg-surface text-ink transition-colors hover:bg-paper-tint disabled:opacity-50',
            field?.invalid ? 'border-danger' : 'border-ink/30',
          )}
        >
          {busy ? <Spinner className="text-[22px]" /> : <Camera size={28} aria-hidden="true" />}
          <span className="font-mono text-[12px] font-bold uppercase tracking-label">
            {t('photo.take')}
          </span>
        </button>
      )}
      {failed && <p className="text-[13px] text-danger">{t('photo.failed')}</p>}
    </div>
  )
}
